/**
 * Shared web-push client helpers used by the Profile settings card and the mobile
 * enable-prompt. Keeps the subscribe flow (VAPID key → pushManager.subscribe → save)
 * in one place. Returns status codes (not translated strings) so callers map to i18n.
 */
import { getPushVapidPublicKey, subscribePush } from './api-client'

export const pushSupported =
  'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window

/** VAPID base64url public key → Uint8Array for pushManager.subscribe(). */
function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = window.atob(b64)
  const out = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out
}

/** True when this browser already holds a push subscription. */
export async function isPushSubscribed(): Promise<boolean> {
  if (!pushSupported) return false
  const reg = await navigator.serviceWorker.ready
  return !!(await reg.pushManager.getSubscription())
}

export type EnablePushResult =
  | { ok: true }
  | { ok: false; code: 'denied' | 'serverOff' | 'error'; detail?: string }

/**
 * Ask permission, subscribe via VAPID, and persist to the API. `renew` drops this browser's current
 * subscription first: for when the server no longer knows it (pruned after a failed send), where
 * subscribing again would only hand back the same dead endpoint.
 */
export async function enablePush(opts: { renew?: boolean } = {}): Promise<EnablePushResult> {
  try {
    const perm = await Notification.requestPermission()
    if (perm !== 'granted') return { ok: false, code: 'denied' }
    const keyRes = await getPushVapidPublicKey()
    if (!keyRes.success || !keyRes.data.publicKey) return { ok: false, code: 'serverOff' }
    const reg = await navigator.serviceWorker.ready
    if (opts.renew) await (await reg.pushManager.getSubscription())?.unsubscribe()
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(keyRes.data.publicKey) as BufferSource,
    })
    const saved = await subscribePush(sub.toJSON())
    if (!saved.success) return { ok: false, code: 'error', detail: saved.error.message }
    return { ok: true }
  } catch (e) {
    return { ok: false, code: 'error', detail: e instanceof Error ? e.message : 'error' }
  }
}
